import { CountryEntity } from 'api/types/entity';
import { useTranslation } from 'libs/i18n';
import { FileType, FileUpload, Stack, Typography } from 'libs/ui';
import { AddressesFields } from 'modules/onboarding/common/components/AddressesFields';

import {
  useFormContext,
  Field,
  MAX_FILES,
  CompanyAddressFormType,
} from '../../../forms/companyAddressForm';

export interface FormContentProps {
  countriesLoading?: boolean;
  countries: CountryEntity[];
  enabledFiles?: boolean;
  files?: FileUpload[];
  onAddFiles?: (file: File[]) => void;
  onRemoveFile?: (file: FileUpload) => void;
}

export const FormContent = ({
  countriesLoading,
  countries,
  enabledFiles,
  files,
  onAddFiles,
  onRemoveFile,
}: FormContentProps) => {
  const { values } = useFormContext();
  const { t } = useTranslation();

  return (
    <Stack spacing={32}>
      <Stack spacing={24} spacingMobile={16}>
        <Typography variant="subtitle" variantMobile="bodyBold">
          {t('onboarding.companyRegistrationAddress.titleRegistration')}
        </Typography>
        <AddressesFields<CompanyAddressFormType>
          apartmentName="apartmentLegal"
          areaName="areaLegal"
          countries={countries}
          countriesLoading={countriesLoading}
          countryName="countryLegal"
          houseNumberName="houseNumberLegal"
          housingName="housingLegal"
          localityName="localityLegal"
          postcodeName="postcodeLegal"
          regionName="regionLegal"
          streetName="streetLegal"
        />
        {enabledFiles && (
          <Field.FilesUpload
            files={files}
            fileTypes={[FileType.pdf]}
            maxFiles={MAX_FILES}
            name="confirmationFileIds"
            title={t('onboarding.companyRegistrationAddress.documentTitle')}
            onAddFiles={onAddFiles}
            onRemoveFile={onRemoveFile}
          />
        )}
      </Stack>

      <Stack spacing={24} spacingMobile={16}>
        <Stack
          direction="row-exceptMobile"
          justifyContent="space-between"
          spacing={24}
          spacingMobile={16}
        >
          <Typography variant="subtitle" variantMobile="bodyBold">
            {t('onboarding.companyRegistrationAddress.titleResidence')}
          </Typography>
          <Stack
            alignItems="center"
            direction="row"
            flexWrap="nowrap"
            justifyContent="space-between"
            spacing={16}
          >
            <Typography
              variant="bodyMedium"
              variantMobile="caption"
              whiteSpace="nowrap"
            >
              {t('onboarding.companyRegistrationAddress.residence.label')}
            </Typography>
            <Field.Switch name="actualAddressIsSame" />
          </Stack>
        </Stack>

        {!values.actualAddressIsSame && (
          <AddressesFields<CompanyAddressFormType>
            apartmentName="apartmentActual"
            areaName="areaActual"
            countries={countries}
            countriesLoading={countriesLoading}
            countryName="countryActual"
            houseNumberName="houseNumberActual"
            housingName="housingActual"
            localityName="localityActual"
            postcodeName="postcodeActual"
            regionName="regionActual"
            streetName="streetActual"
          />
        )}
      </Stack>
    </Stack>
  );
};
